'use client';

import { useState } from 'react';
import Link from 'next/link';
import { TrendingUp, TrendingDown, Minus, Star, ArrowLeft, ExternalLink, AlertCircle } from 'lucide-react';
import { Top25YearData, compareYears, AVAILABLE_YEARS } from '../data/top25-all-years';

interface Top25ClientProps {
  yearData: Record<number, Top25YearData>;
  initialYear: number;
}

function getCWEUrl(cweId: string): string {
  const numericId = cweId.replace('CWE-', '');
  return `/tools/security/cwe-lookup/cwe-${numericId}`;
}

export default function Top25Client({ yearData, initialYear }: Top25ClientProps) {
  const [selectedYear, setSelectedYear] = useState(initialYear);

  const currentData = yearData[selectedYear];

  // Get previous year for comparison
  const previousYearIndex = AVAILABLE_YEARS.indexOf(selectedYear) - 1;
  const previousYear = previousYearIndex >= 0 ? AVAILABLE_YEARS[previousYearIndex] : null;
  const comparison = previousYear ? compareYears(previousYear, selectedYear) : null;

  const getTrendIndicator = (cweId: string) => {
    if (!comparison) return null;

    const isNew = comparison.newInYear2.includes(cweId);
    const prevEntry = comparison.year1Data.entries.find((e) => e.cweId === cweId);
    const currEntry = currentData.entries.find((e) => e.cweId === cweId);

    if (isNew) {
      return {
        icon: <Star className="w-4 h-4" />,
        label: 'NEW',
        color: 'text-blue-600 dark:text-blue-400',
        bgColor: 'bg-blue-100 dark:bg-blue-900/30',
      };
    }

    if (!prevEntry || !currEntry) return null;

    const rankChange = prevEntry.rank - currEntry.rank;

    if (rankChange > 0) {
      // Improved (moved up in rank)
      return {
        icon: <TrendingUp className="w-4 h-4" />,
        label: `↑${rankChange}`,
        color: 'text-green-600 dark:text-green-400',
        bgColor: 'bg-green-100 dark:bg-green-900/30',
      };
    } else if (rankChange < 0) {
      // Worsened (moved down in rank)
      return {
        icon: <TrendingDown className="w-4 h-4" />,
        label: `↓${Math.abs(rankChange)}`,
        color: 'text-red-600 dark:text-red-400',
        bgColor: 'bg-red-100 dark:bg-red-900/30',
      };
    } else {
      // Stable
      return {
        icon: <Minus className="w-4 h-4" />,
        label: '—',
        color: 'text-gray-600 dark:text-gray-400',
        bgColor: 'bg-gray-100 dark:bg-gray-800',
      };
    }
  };

  const getRankBadgeClass = (rank: number) => {
    if (rank === 1) return 'bg-gradient-to-br from-yellow-400 to-yellow-500 text-yellow-900 shadow-lg';
    if (rank === 2) return 'bg-gradient-to-br from-gray-300 to-gray-400 text-gray-800 shadow-md';
    if (rank === 3) return 'bg-gradient-to-br from-orange-400 to-orange-500 text-orange-900 shadow-md';
    return 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300';
  };

  const getRowClass = (rank: number, isNew: boolean) => {
    if (rank === 1) return 'bg-yellow-50/50 dark:bg-yellow-900/10 border-l-4 border-yellow-500';
    if (rank === 2) return 'bg-gray-50/50 dark:bg-gray-800/30 border-l-4 border-gray-400';
    if (rank === 3) return 'bg-orange-50/50 dark:bg-orange-900/10 border-l-4 border-orange-500';
    if (isNew) return 'bg-blue-50/50 dark:bg-blue-900/10';
    return '';
  };

  return (
    <div className="w-full">
      {/* Header with Back Button */}
      <div className="mb-6">
        <Link
          href="/tools/security/cwe-lookup"
          className="inline-flex items-center gap-2 text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to CWE Lookup
        </Link>
      </div>

      {/* Main Card */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden shadow-lg">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-indigo-600 px-6 py-6">
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="w-6 h-6 text-white" />
                <h1 className="text-2xl font-bold text-white">
                  CWE Top 25 Most Dangerous Software Weaknesses
                </h1>
              </div>
              <p className="text-purple-100 text-sm">
                {currentData.dataSource} • Released: {new Date(currentData.releaseDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
              </p>
              <p className="text-purple-100 text-sm">
                Based on analysis of {currentData.totalCVEs.toLocaleString()} CVE records
              </p>
            </div>

            <div className="flex items-center gap-3">
              {/* Year Selector */}
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                className="px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:ring-2 focus:ring-white/50 focus:border-transparent backdrop-blur"
              >
                {AVAILABLE_YEARS.sort((a, b) => b - a).map((year) => (
                  <option key={year} value={year} className="bg-gray-800 text-white">
                    {year} List
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Stats Banner */}
        {comparison && (
          <div className="bg-blue-50 dark:bg-blue-900/20 border-b border-blue-200 dark:border-blue-800 px-6 py-4">
            <div className="flex items-center gap-2 mb-2">
              <AlertCircle className="w-4 h-4 text-blue-600 dark:text-blue-400" />
              <h3 className="text-sm font-semibold text-blue-900 dark:text-blue-100">
                Changes from {previousYear}
              </h3>
            </div>
            <div className="flex flex-wrap gap-4 text-sm text-blue-800 dark:text-blue-200">
              <span className="flex items-center gap-1">
                <span className="font-semibold">{comparison.commonCWEs.length}</span> Common
              </span>
              <span className="flex items-center gap-1">
                <Star className="w-4 h-4" />
                <span className="font-semibold">{comparison.newInYear2.length}</span> New Entries
              </span>
              <span className="flex items-center gap-1">
                <span className="font-semibold">{comparison.droppedFromYear2.length}</span> Dropped
              </span>
            </div>
          </div>
        )}

        {/* Desktop Table View */}
        <div className="hidden md:block p-6">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-gray-200 dark:border-gray-700">
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">
                    Rank
                  </th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">
                    CWE ID
                  </th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">
                    Name
                  </th>
                  <th className="text-center py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">
                    Score
                  </th>
                  <th className="text-center py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">
                    CVE Count
                  </th>
                  <th className="text-center py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">
                    Avg CVSS
                  </th>
                  {comparison && (
                    <th className="text-center py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">
                      Trend
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {currentData.entries.map((entry) => {
                  const trend = getTrendIndicator(entry.cweId);
                  const isNew = comparison?.newInYear2.includes(entry.cweId) || false;

                  return (
                    <tr
                      key={entry.cweId}
                      className={`border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors ${getRowClass(entry.rank, isNew)}`}
                    >
                      <td className="py-4 px-4">
                        <span
                          className={`inline-flex items-center justify-center w-10 h-10 rounded-full text-sm font-bold ${getRankBadgeClass(entry.rank)}`}
                        >
                          {entry.rank}
                        </span>
                      </td>
                      <td className="py-4 px-4">
                        <Link
                          href={getCWEUrl(entry.cweId)}
                          className="font-mono text-sm text-blue-600 dark:text-blue-400 font-semibold hover:underline"
                        >
                          {entry.cweId}
                        </Link>
                      </td>
                      <td className="py-4 px-4">
                        <Link
                          href={getCWEUrl(entry.cweId)}
                          className="text-sm text-gray-900 dark:text-gray-100 font-medium hover:text-blue-600 dark:hover:text-blue-400"
                        >
                          {entry.name}
                        </Link>
                      </td>
                      <td className="py-4 px-4 text-center">
                        <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                          {entry.score.toFixed(2)}
                        </span>
                      </td>
                      <td className="py-4 px-4 text-center">
                        <span className="text-sm text-gray-600 dark:text-gray-400 font-medium">
                          {entry.cveCount.toLocaleString()}
                        </span>
                      </td>
                      <td className="py-4 px-4 text-center">
                        <span
                          className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${
                            entry.avgCVSS >= 8
                              ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                              : entry.avgCVSS >= 6
                              ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300'
                              : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'
                          }`}
                        >
                          {entry.avgCVSS.toFixed(1)}
                        </span>
                      </td>
                      {comparison && trend && (
                        <td className="py-4 px-4 text-center">
                          <span
                            className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold ${trend.bgColor} ${trend.color}`}
                          >
                            {trend.icon}
                            {trend.label}
                          </span>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Mobile Card View */}
        <div className="md:hidden p-4 space-y-3">
          {currentData.entries.map((entry) => {
            const trend = getTrendIndicator(entry.cweId);
            const isNew = comparison?.newInYear2.includes(entry.cweId) || false;

            return (
              <Link
                key={entry.cweId}
                href={getCWEUrl(entry.cweId)}
                className={`block bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:shadow-md transition-shadow ${getRowClass(entry.rank, isNew)}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <span
                      className={`inline-flex items-center justify-center w-10 h-10 rounded-full text-sm font-bold ${getRankBadgeClass(entry.rank)}`}
                    >
                      {entry.rank}
                    </span>
                    <div>
                      <div className="font-mono text-sm text-blue-600 dark:text-blue-400 font-semibold">
                        {entry.cweId}
                      </div>
                      {comparison && trend && (
                        <span
                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${trend.bgColor} ${trend.color} mt-1`}
                        >
                          {trend.icon}
                          {trend.label}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">
                  {entry.name}
                </h3>

                <div className="grid grid-cols-3 gap-2 text-xs">
                  <div>
                    <div className="text-gray-500 dark:text-gray-400 mb-1">Score</div>
                    <div className="font-semibold text-gray-700 dark:text-gray-300">
                      {entry.score.toFixed(2)}
                    </div>
                  </div>
                  <div>
                    <div className="text-gray-500 dark:text-gray-400 mb-1">CVEs</div>
                    <div className="font-semibold text-gray-700 dark:text-gray-300">
                      {entry.cveCount.toLocaleString()}
                    </div>
                  </div>
                  <div>
                    <div className="text-gray-500 dark:text-gray-400 mb-1">Avg CVSS</div>
                    <div>
                      <span
                        className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${
                          entry.avgCVSS >= 8
                            ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                            : entry.avgCVSS >= 6
                            ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300'
                            : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'
                        }`}
                      >
                        {entry.avgCVSS.toFixed(1)}
                      </span>
                    </div>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 dark:border-gray-700 px-6 py-4 bg-gray-50 dark:bg-gray-900/50">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Click any row to view detailed information about that weakness
            </p>
            <a
              href={`https://cwe.mitre.org/top25/archive/${selectedYear}/${selectedYear}_top25_list.html`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 font-medium"
            >
              View official {selectedYear} list on MITRE
              <ExternalLink className="w-4 h-4" />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
